import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, Search, RefreshCw, ChevronLeft, ChevronRight, 
  Phone, MapPin, Building2, CheckCircle, XCircle, Package,
  Navigation, Calendar, Settings
} from "lucide-react";

const PharmeasyRiders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [riders, setRiders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Default capacity setting
  const [defaultCapacity, setDefaultCapacity] = useState(20);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [tempCapacity, setTempCapacity] = useState(20);

  const pageSize = 50;

  const fetchRiders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: pageSize });
      if (statusFilter) params.set("status", statusFilter);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      
      const res = await axios.get(`${API}/pharmeasy-orders/riders?${params}`);
      setRiders(res.data.data);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setTotalAll(res.data.total_all ?? res.data.total);
      setActiveCount(res.data.active_count ?? 0);
      setInactiveCount(res.data.inactive_count ?? 0);
    } catch (e) { 
      toast.error("Failed to fetch riders"); 
    }
    finally { setLoading(false); }
  }, [page, searchQuery, statusFilter]);

  useEffect(() => { fetchRiders(); }, [fetchRiders]);

  // Fetch default capacity setting
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/pharmeasy-orders/settings/rider-capacity`);
        if (res.data.default_capacity) {
          setDefaultCapacity(res.data.default_capacity);
          setTempCapacity(res.data.default_capacity);
        }
      } catch (e) {
        // Use default if not set
      }
    };
    fetchSettings();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-riders`);
      toast.success(`Sync complete: ${res.data.total_new} new, ${res.data.total_updated} updated`);
      setPage(1);
      fetchRiders();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object") toast.error(detail.error || "Sync failed");
      else toast.error(detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRiders();
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  // Save default capacity
  const saveDefaultCapacity = async () => {
    try {
      await axios.post(`${API}/pharmeasy-orders/settings/rider-capacity`, {
        default_capacity: tempCapacity
      });
      setDefaultCapacity(tempCapacity);
      toast.success("Default capacity updated");
      setShowSettingsDialog(false);
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-riders">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Pharmeasy Riders</h1>
            <p className="text-slate-600">Manage delivery riders (sprinters) synced from BOLT API</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowSettingsDialog(true)} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Default Settings
            </Button>
            <Button onClick={handleSync} disabled={syncing} style={{ backgroundColor: "#1B8C3D" }} data-testid="sync-riders-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing..." : "Sync Riders"}
            </Button>
          </div>
        </div>

        {/* Default Capacity Info Card */}
        <Card className="border-0 shadow-md bg-blue-50 border-l-4 border-l-blue-400">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-blue-600" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Default Parcel Capacity: <span className="text-lg font-bold">{defaultCapacity}</span> parcels/rider</p>
                  <p className="text-xs text-blue-600">This will be used as default in trip planning</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setTempCapacity(defaultCapacity); setShowSettingsDialog(true); }} className="text-blue-700 border-blue-300 hover:bg-blue-100">
                <Settings className="w-4 h-4 mr-1" /> Change
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Total Riders</p>
                  <p className="text-2xl font-bold text-slate-800">{totalAll}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${statusFilter === "ACTIVE" ? "ring-2 ring-emerald-500" : ""}`}
            onClick={() => { setStatusFilter(statusFilter === "ACTIVE" ? "" : "ACTIVE"); setPage(1); }}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-emerald-600 uppercase">Active</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    {activeCount || "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${statusFilter === "INACTIVE" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => { setStatusFilter(statusFilter === "INACTIVE" ? "" : "INACTIVE"); setPage(1); }}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-red-600 uppercase">Inactive</p>
                  <p className="text-2xl font-bold text-red-800">
                    {inactiveCount || "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Table */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex gap-3 items-center">
                <Badge className={`px-3 py-1.5 text-sm cursor-default ${
                  statusFilter === "ACTIVE" ? "bg-emerald-100 text-emerald-800" :
                  statusFilter === "INACTIVE" ? "bg-red-100 text-red-800" :
                  "bg-slate-100 text-slate-800"
                }`}>
                  {statusFilter || "All"} ({total})
                </Badge>
                {statusFilter && (
                  <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(""); setPage(1); }} className="text-slate-500">
                    Clear filter
                  </Button>
                )}
              </div>
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search name, mobile, email..."
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-72" 
                    data-testid="rider-search-input" 
                  />
                </div>
                <Button type="submit" variant="outline" data-testid="rider-search-btn">Search</Button>
              </form>
            </div>

            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading riders...</div>
            ) : riders.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-500 mb-1">
                  {searchQuery || statusFilter ? "No riders match your search" : "No riders found"}
                </p>
                <p className="text-sm text-slate-400 mb-4">
                  {searchQuery || statusFilter ? "Try adjusting your filters" : 'Click "Sync Riders" to fetch from Pharmeasy'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Mobile</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">City</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Hub</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Last Synced</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {riders.map((rider, idx) => (
                        <tr key={rider._id} className="hover:bg-slate-50" data-testid={`rider-row-${rider._id}`}>
                          <td className="px-4 py-3 align-middle text-sm text-slate-500">{(page - 1) * pageSize + idx + 1}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-semibold text-sm">
                                {rider.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <div>
                                <span className="font-medium text-slate-800">{rider.name || "—"}</span>
                                <p className="text-xs text-slate-400">{rider.email || "—"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex items-center gap-1 text-slate-700">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-mono">+{rider.mobile_country_code || "91"} {rider.mobile || "—"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex items-center gap-1 text-slate-600">
                              <MapPin className="w-3.5 h-3.5 text-slate-400" />
                              {rider.service_city || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Building2 className="w-3.5 h-3.5 text-slate-400" />
                              {rider.assigned_asset_name || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <Badge className={rider.bolt_status === "ACTIVE"
                              ? "bg-emerald-100 text-emerald-700" 
                              : "bg-red-100 text-red-700"
                            }>
                              {rider.bolt_status || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-600 whitespace-nowrap">
                            <div>{formatDate(rider.bolt_synced_at).split(", ")[0]}</div>
                            <div className="text-xs text-slate-500">{formatDate(rider.bolt_synced_at).split(", ")[1]}</div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => navigate(`/pharmeasy/riders/${rider._id}/location`)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                title="View Location & History"
                              >
                                <Navigation className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => navigate(`/pharmeasy/riders/${rider._id}/attendance`)}
                                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                title="View Attendance"
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page-btn">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="flex items-center px-3 text-sm text-slate-600">Page {page} of {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page-btn">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-0 shadow-md bg-orange-50 border-l-4 border-l-orange-400">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium">Rider Account Info</p>
                <p className="mt-1">
                  Each rider is automatically created as a user with role <code className="bg-orange-100 px-1 rounded">pharmeasy_rider</code>.
                  They can login using their mobile number as both email (<code className="bg-orange-100 px-1 rounded">{"{mobile}"}@edgistify.com</code>) and password.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Default Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Default Rider Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Default Maximum Parcel Capacity</Label>
              <p className="text-xs text-slate-500 mb-2">This will be used as default max shipments per trip in trip planning</p>
              <Input 
                type="number" 
                min={1} 
                max={100}
                value={tempCapacity} 
                onChange={(e) => setTempCapacity(parseInt(e.target.value) || 20)}
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
              <Button onClick={saveDefaultCapacity} style={{ backgroundColor: "#1B8C3D" }}>Save Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default PharmeasyRiders;
